import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useExchangeRate } from '../../hooks/useExchangeRate';
import { useTransactionStatus } from '../../hooks/useTransactionStatus';
import { TransactionStatusDisplay } from './TransactionStatusDisplay';
import { enhancedAddressValidation, getAddressRequirements } from '../../utils/walletValidation';

interface Currency {
  ticker: string;
  name: string;
  network?: string;
  isActive?: boolean;
}

// Popular coins with their networks - these appear first
const POPULAR_COINS = [
  { ticker: 'btc', name: 'Bitcoin', priority: 100 },
  { ticker: 'ltc', name: 'Litecoin', priority: 95 },
  { ticker: 'bnb', name: 'BNB', priority: 90 },
  { ticker: 'xaut', name: 'XAUT', priority: 85 },
  { ticker: 'sol', name: 'Solana', priority: 80 },
  { ticker: 'eth', name: 'Ethereum', priority: 75 },
  { ticker: 'xmr', name: 'Monero', priority: 70 },
  // USDC variants
  { ticker: 'usdc_eth', name: 'USD Coin', network: 'ETH', priority: 65 },
  { ticker: 'usdc_trx', name: 'USD Coin', network: 'TRON', priority: 64 },
  { ticker: 'usdc_arbitrum', name: 'USD Coin', network: 'ARB', priority: 63 },
  { ticker: 'usdc_sol', name: 'USD Coin', network: 'SOL', priority: 62 },
  { ticker: 'usdc_bsc', name: 'USD Coin', network: 'BSC', priority: 61 },
  // USDT variants
  { ticker: 'usdt_eth', name: 'Tether', network: 'ETH', priority: 60 },
  { ticker: 'usdt_trx', name: 'Tether', network: 'TRON', priority: 59 },
  { ticker: 'usdt_arbitrum', name: 'Tether', network: 'ARB', priority: 58 },
  { ticker: 'usdt_sol', name: 'Tether', network: 'SOL', priority: 57 },
  { ticker: 'usdt_bsc', name: 'Tether', network: 'BSC', priority: 56 },
];

const API_BASE = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3000/api';

export const ExchangeForm: React.FC = () => {
  const [fromCurrency, setFromCurrency] = useState('btc');
  const [toCurrency, setToCurrency] = useState('usdt_eth');
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [minAmount, setMinAmount] = useState<number | null>(null);
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<any>(null);
  const [payoutAddress, setPayoutAddress] = useState('');
  const [fromAmount, setFromAmount] = useState('0.01');
  const [addressError, setAddressError] = useState<string | null>(null);
  const [addressValidationMessage, setAddressValidationMessage] = useState<string>('');
  const [view, setView] = useState<'form' | 'status'>('form');
  const [apiHealth, setApiHealth] = useState<'checking' | 'healthy' | 'down'>('checking');
  const [dbHealth, setDbHealth] = useState<'checking' | 'healthy' | 'down'>('checking');

  // Search state
  const [fromSearch, setFromSearch] = useState('');
  const [toSearch, setToSearch] = useState('');

  // Refs for persistence
  const fromListRef = useRef<HTMLDivElement>(null);
  const toListRef = useRef<HTMLDivElement>(null);

  const { exchangeRate: rateData, updateExchange } = useExchangeRate(
    fromCurrency,
    toCurrency,
    parseFloat(fromAmount) || 0,
    { refreshInterval: 10000, autoRefresh: true }
  );

  const { status: transactionStatus, isLoading: statusLoading, error: statusError, refreshStatus, isAutoRefreshEnabled: statusAutoRefresh } = useTransactionStatus(
    success?.id || null,
    { refreshInterval: 10000, autoRefresh: true, maxRetries: 120 }
  );

  // Create sorted currencies with popular ones first
  const sortedCurrencies = useMemo(() => {
    const priorityMap = new Map<string, number>();

    POPULAR_COINS.forEach(c => {
      priorityMap.set(c.ticker, c.priority);
    });

    return [...currencies].sort((a, b) => {
      const aPriority = priorityMap.get(a.ticker) || 0;
      const bPriority = priorityMap.get(b.ticker) || 0;

      if (aPriority !== bPriority) return bPriority - aPriority;

      // Same priority? Alphabetical
      return a.ticker.localeCompare(b.ticker);
    });
  }, [currencies]);

  // Filter currencies
  const filteredFromCurrencies = useMemo(() => {
    if (!fromSearch.trim()) return sortedCurrencies;
    const search = fromSearch.toLowerCase();
    return sortedCurrencies.filter(c =>
      c.ticker.toLowerCase().includes(search) ||
      c.name.toLowerCase().includes(search) ||
      (c.network && c.network.toLowerCase().includes(search))
    );
  }, [sortedCurrencies, fromSearch]);

  const filteredToCurrencies = useMemo(() => {
    const filtered = sortedCurrencies.filter(c => c.ticker !== fromCurrency);
    if (!toSearch.trim()) return filtered;
    const search = toSearch.toLowerCase();
    return filtered.filter(c =>
      c.ticker.toLowerCase().includes(search) ||
      c.name.toLowerCase().includes(search) ||
      (c.network && c.network.toLowerCase().includes(search))
    );
  }, [sortedCurrencies, toSearch, fromCurrency]);

  // Get display label
  const getCurrencyLabel = (c: Currency) => {
    const baseLabel = c.ticker.toUpperCase();
    if (c.network && c.network !== c.ticker) {
      return `${baseLabel} (${c.network.toUpperCase()})`;
    }
    return baseLabel;
  };

  // Get network badge color
  const getNetworkColor = (network: string) => {
    const colors: Record<string, string> = {
      'ETH': '#627eea',
      'TRON': '#e0003c',
      'ARB': '#f5851b',
      'SOL': '#14f195',
      'BSC': '#f0b90b',
      'OP': '#f5851b',
      'AVAX': '#e84142',
      'MATIC': '#8247e3',
    };
    return colors[network.toUpperCase()] || '#666';
  };

  // Restore scroll position
  useEffect(() => {
    if (fromListRef.current && view === 'form') {
      const savedFromScroll = sessionStorage.getItem('fromListScroll');
      if (savedFromScroll) {
        fromListRef.current.scrollTop = parseInt(savedFromScroll);
      }
    }
  }, [view]);

  // Save scroll position
  const saveFromScroll = () => {
    if (fromListRef.current) {
      sessionStorage.setItem('fromListScroll', fromListRef.current.scrollTop.toString());
    }
  };

  const saveToScroll = () => {
    if (toListRef.current) {
      sessionStorage.setItem('toListScroll', toListRef.current.scrollTop.toString());
    }
  };

  // Check connection
  useEffect(() => {
    checkConnection();
  }, []);

  // Fetch currencies
  useEffect(() => {
    fetchCurrencies();
  }, []);

  // Update rates
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (fromCurrency && toCurrency && fromAmount && currencies.length > 0) {
      updateExchange(fromCurrency, toCurrency, parseFloat(fromAmount), true);
    }
  }, [fromCurrency, toCurrency, fromAmount, currencies]);

  useEffect(() => {
    if (payoutAddress && toCurrency) {
      setAddressError(null);
      setAddressValidationMessage('');
      validateAddress(payoutAddress, toCurrency);
    }
  }, [toCurrency]);

  useEffect(() => {
    if (fromCurrency && toCurrency) {
      fetchMinAmount();
    }
  }, [fromCurrency, toCurrency]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const checkConnection = async () => {
    try {
      const response = await fetch(`${API_BASE}/health`);
      if (response.ok) {
        const data = await response.json();
        // If status is 'ok', both API and DB are healthy
        if (data.status === 'ok' || data.api?.status === 'ok') {
          setApiHealth('healthy');
          setDbHealth('healthy');
        } else {
          setApiHealth('down');
          setDbHealth('down');
        }
      } else {
        setApiHealth('down');
        setDbHealth('down');
      }
    } catch {
      setApiHealth('down');
      setDbHealth('down');
    }
  };

  // Periodic health check
  useEffect(() => {
    checkConnection();
    const interval = setInterval(checkConnection, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchCurrencies = async () => {
    try {
      const response = await fetch(`${API_BASE}/currencies`);
      const data = await response.json();

      if (data.success && Array.isArray(data.data)) {
        const uniqueCurrencies = new Map<string, Currency>();

        data.data.forEach((currency: any) => {
          const uniqueTicker = currency.network && currency.network !== currency.ticker
            ? `${currency.ticker}_${currency.network}`
            : currency.ticker;

          if (!uniqueCurrencies.has(uniqueTicker)) {
            uniqueCurrencies.set(uniqueTicker, {
              ticker: uniqueTicker,
              name: currency.name,
              network: currency.network || '',
              isActive: currency.isActive !== false,
            });
          }
        });

        const sorted = Array.from(uniqueCurrencies.values()).sort((a, b) => a.ticker.localeCompare(b.ticker));
        setCurrencies(sorted);
      }
    } catch (err: any) {
      setError(`API Error: ${err.message}`);
      setCurrencies([
        { ticker: 'btc', name: 'Bitcoin', network: '', isActive: true },
        { ticker: 'eth', name: 'Ethereum', network: '', isActive: true },
      ]);
    }
  };

  const fetchMinAmount = async () => {
    if (!fromCurrency || !toCurrency) return;

    try {
      const response = await fetch(`${API_BASE}/min-amount?fromCurrency=${fromCurrency}&toCurrency=${toCurrency}`);
      const data = await response.json();

      if (data.success && data.data.minAmount) {
        setMinAmount(data.data.minAmount);
      }
    } catch (err) {
      console.error('[ExchangeForm] Error fetching min amount:', err);
    }
  };

  const validateAddress = (address: string, currency: string): boolean => {
    if (!address || !currency) return false;

    const validation = enhancedAddressValidation(address, currency);

    if (validation.isValid) {
      setAddressError(null);
      setAddressValidationMessage('OK');
      return true;
    } else {
      setAddressError(validation.message || 'INVALID');
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!fromCurrency || !toCurrency || !fromAmount || !payoutAddress) {
      setError('ERROR: ALL FIELDS REQUIRED');
      return;
    }

    if (!validateAddress(payoutAddress, toCurrency)) {
      setError('ERROR: INVALID ADDRESS');
      return;
    }

    if (minAmount && parseFloat(fromAmount) < minAmount) {
      setError(`ERROR: MIN AMOUNT ${minAmount} ${fromCurrency.toUpperCase()}`);
      return;
    }

    setLoadingCreate(true);

    try {
      // Demo mode - create mock transaction
      if (false) { // Disabled demo mode
        await new Promise(resolve => setTimeout(resolve, 1000));

        const mockTransaction = {
          id: `demo_${Date.now()}`,
          fromCurrency: fromCurrency.toLowerCase(),
          toCurrency: toCurrency.toLowerCase(),
          fromAmount,
          toAmount: rateData.toAmount.toFixed(8),
          payoutAddress,
          payinAddress: 'bc1q' + Math.random().toString(36).substring(2, 40) + 'x',
          expectedAmount: fromAmount,
          status: 'waiting',
          createdAt: new Date().toISOString(),
        };

        setSuccess(mockTransaction);
        setView('status');
        setLoadingCreate(false);
        return;
      }

      // Real API call
      const response = await fetch(`${API_BASE}/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromCurrency: fromCurrency.toLowerCase(),
          toCurrency: toCurrency.toLowerCase(),
          fromAmount,
          payoutAddress,
          flow: 'standard'
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess({ ...data.data, toAmountEstimated: rateData.toAmount });
        setView('status');
      } else {
        setError(data.error || 'ERROR: TRANSACTION FAILED');
      }
    } catch (err: any) {
      setError(`ERROR: ${err.message}`);
    } finally {
      setLoadingCreate(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Currency list component
  const CurrencyList = ({
    type,
    selected,
    onSelect,
    search,
    setSearch,
    filtered,
    listRef
  }: {
    type: 'from' | 'to';
    selected: string;
    onSelect: (ticker: string) => void;
    search: string;
    setSearch: (s: string) => void;
    filtered: Currency[];
    listRef: React.RefObject<HTMLDivElement | null>;
  }) => (
    <div className="terminal-list-container">
      <div className="terminal-list-header">
        <div className="terminal-list-title">{type === 'from' ? '> FROM' : '> TO'}</div>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="SEARCH..."
          className="terminal-search-input"
          autoComplete="off"
        />
        <div className="terminal-list-count">
          {filtered.length} RESULTS
        </div>
      </div>

      <div
        className="terminal-list"
        ref={listRef}
        onScroll={type === 'from' ? saveFromScroll : saveToScroll}
      >
        {filtered.map((c) => {
          const isSelected = c.ticker === selected;
          const priority = POPULAR_COINS.find(p => p.ticker === c.ticker)?.priority || 0;
          const isTopCoin = priority >= 50;

          return (
            <div
              key={c.ticker}
              onClick={() => onSelect(c.ticker)}
              className={`terminal-list-item ${isSelected ? 'terminal-list-selected' : ''} ${isTopCoin ? 'terminal-list-top' : ''}`}
            >
              <div className="terminal-list-left">
                <div className="terminal-list-ticker">{getCurrencyLabel(c)}</div>
                {c.network && c.network !== c.ticker && (
                  <div
                    className="terminal-network-badge"
                    style={{ backgroundColor: getNetworkColor(c.network) }}
                  >
                    {c.network.toUpperCase()}
                  </div>
                )}
              </div>
              <div className="terminal-list-name">{c.name}</div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="terminal-list-empty">
            NO COINS FOUND
          </div>
        )}
      </div>
    </div>
  );

  const HealthIndicators = () => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span className={`terminal-health-indicator ${apiHealth}`}></span>
          <span style={{ fontSize: 'clamp(10px, 2vw, 11px)', color: '#888' }}>API</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span className={`terminal-health-indicator ${dbHealth}`}></span>
          <span style={{ fontSize: 'clamp(10px, 2vw, 11px)', color: '#888' }}>DB</span>
        </div>
      </div>
    );
  };

  // View mode for restore scroll tracking
  let viewMode: 'form' | 'status' = view;

  if (viewMode === 'status' && success) {
    return (
      <div className="terminal-container">
        <div className="terminal-wrapper">
          {/* Header */}
          <div className="terminal-header">
            <div className="terminal-logo">
              <img src="/logo-nobg.png" alt="SWAPME.SPACE Logo" />
            </div>
            <h1>SWAPME.SPACE</h1>
            <div className="terminal-subtitle">
              <HealthIndicators />
              <span>|</span>
              <span>INSTANT CRYPTO EXCHANGE</span>
            </div>
          </div>

          {/* Main Content */}
          <div className="terminal-main">
            <div className="terminal-content">
              <div style={{ marginBottom: 'clamp(12px, 2vw, 16px)' }}>
                <TransactionStatusDisplay
                  transaction={success}
                  status={transactionStatus}
                  isLoading={statusLoading}
                  error={statusError}
                  onRefresh={refreshStatus}
                  onCopyAddress={copyToClipboard}
                  isAutoRefreshEnabled={statusAutoRefresh}
                />
              </div>
              <button
                onClick={() => {
                  setView('form');
                  setSuccess(null);
                  setPayoutAddress('');
                }}
                className="terminal-button terminal-button-full"
              >
                [ NEW EXCHANGE ]
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="terminal-footer">
            <div className="terminal-footer-content">
              <div className="terminal-health-status">
                <div className="terminal-health-item">
                  <span className={`terminal-health-indicator ${apiHealth}`}></span>
                  <span className="terminal-health-label">API</span>
                </div>
                <div className="terminal-health-item">
                  <span className={`terminal-health-indicator ${dbHealth}`}></span>
                  <span className="terminal-health-label">DATABASE</span>
                </div>
              </div>
              <div className="terminal-links">
                <a href="https://changenow.io" target="_blank" rel="noopener noreferrer">POWERED BY CHANGENOW.IO</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="terminal-container">
      <div className="terminal-wrapper">
        {/* Header */}
        <div className="terminal-header">
          <div className="terminal-logo">
            <img src="/logo-nobg.png" alt="SWAPME.SPACE Logo" />
          </div>
          <h1>SWAPME.SPACE</h1>
          <div className="terminal-subtitle">
            <HealthIndicators />
            <span>|</span>
            <span>INSTANT CRYPTO EXCHANGE</span>
          </div>
        </div>

        {/* Main Content */}
        <div className="terminal-main">
          <div className="terminal-content">
            {/* Error */}
            {error && (
              <div style={{ marginBottom: 'clamp(12px, 2vw, 16px)' }} className="terminal-error">{error}</div>
            )}

            {/* Rate */}
            {rateData.rate > 0 && (
              <div style={{ marginBottom: 'clamp(12px, 2vw, 16px)' }} className="terminal-info">
                1 {rateData.fromCurrency.toUpperCase()} = {rateData.rate.toFixed(8)} {rateData.toCurrency.toUpperCase()}
              </div>
            )}

            <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 'clamp(12px, 2vw, 16px)' }} className="terminal-form-grid">
            {/* Left column - Source */}
            <div className="terminal-form-col">
              <CurrencyList
                type="from"
                selected={fromCurrency}
                onSelect={setFromCurrency}
                search={fromSearch}
                setSearch={setFromSearch}
                filtered={filteredFromCurrencies}
                listRef={fromListRef}
              />

              <div className="terminal-field">
                <div className="terminal-label">&gt; AMOUNT:</div>
                <input
                  type="text"
                  value={fromAmount}
                  onChange={(e) => {
                    if (/^\d*\.?\d*$/.test(e.target.value)) {
                      setFromAmount(e.target.value);
                    }
                  }}
                  className="terminal-input"
                />
                {minAmount && (
                  <div className="terminal-warning">MIN: {minAmount} {fromCurrency.toUpperCase()}</div>
                )}
              </div>
            </div>

            {/* Swap button */}
            <div className="terminal-swap-container">
              <button
                type="button"
                onClick={() => {
                  const temp = fromCurrency;
                  setFromCurrency(toCurrency);
                  setToCurrency(temp);
                  setFromSearch('');
                  setToSearch('');
                }}
                className="terminal-swap-btn"
              >
                [ SWAP ]
              </button>
            </div>

            {/* Right column - Destination */}
            <div className="terminal-form-col">
              <CurrencyList
                type="to"
                selected={toCurrency}
                onSelect={setToCurrency}
                search={toSearch}
                setSearch={setToSearch}
                filtered={filteredToCurrencies}
                listRef={toListRef}
              />

              <div className="terminal-field">
                <div className="terminal-label">&gt; RECEIVE:</div>
                <div className="terminal-amount-display-big">
                  {rateData.toAmount > 0 ? `${rateData.toAmount.toFixed(8)} ${toCurrency.toUpperCase()}` : `0.00000000 ${toCurrency.toUpperCase()}`}
                </div>
              </div>
            </div>
          </div>

          {/* Address - Big and Centered */}
          <div style={{ marginBottom: 'clamp(12px, 2vw, 16px)' }} className="terminal-address-field">
            <div className="terminal-label">DESTINATION ADDRESS</div>
            <input
              type="text"
              value={payoutAddress}
              onChange={(e) => {
                setPayoutAddress(e.target.value);
                if (addressError) setAddressError(null);
                if (e.target.value && toCurrency) {
                  setTimeout(() => validateAddress(e.target.value, toCurrency), 300);
                }
              }}
              placeholder={getAddressRequirements(toCurrency)}
              className={`terminal-address-input ${addressError ? 'terminal-input-error' : ''}`}
            />
            {addressError && <div className="terminal-error-small">{addressError}</div>}
            {addressValidationMessage && !addressError && <div className="terminal-success-small">OK</div>}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loadingCreate || !fromCurrency || !toCurrency || !fromAmount || !payoutAddress || !!addressError}
            className="terminal-button terminal-button-primary terminal-button-full"
          >
            {loadingCreate ? 'PROCESSING...' : '[ EXCHANGE ]'}
          </button>
            </form>
          </div>
        </div>

        {/* HOW TO Section */}
        <section className="how-to-section">
          <div className="nokyc-badge">100% NO-KYC • ANONYMOUS • SECURE</div>
          <h2 className="how-to-title">How It Works</h2>
          <div className="how-to-grid">
            <div className="how-to-card">
              <div className="how-to-step">1</div>
              <h3 className="how-to-card-title">Select Currencies</h3>
              <p className="how-to-card-text">Choose your source and destination cryptocurrencies from our extensive list of supported assets. We support all major networks.</p>
            </div>
            <div className="how-to-card">
              <div className="how-to-step">2</div>
              <h3 className="how-to-card-title">Enter Amount</h3>
              <p className="how-to-card-text">Specify the amount you want to exchange. Our system automatically calculates the best rate from our partner exchanges.</p>
            </div>
            <div className="how-to-card">
              <div className="how-to-step">3</div>
              <h3 className="how-to-card-title">Provide Address</h3>
              <p className="how-to-card-text">Enter your destination wallet address. We validate addresses for each network to ensure your funds arrive safely.</p>
            </div>
            <div className="how-to-card">
              <div className="how-to-step">4</div>
              <h3 className="how-to-card-title">Complete Exchange</h3>
              <p className="how-to-card-text">Send your funds to the provided deposit address. Once confirmed, we instantly send the exchanged amount to your wallet.</p>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="features-section">
          <h2 className="how-to-title">Why Choose SWAPME.SPACE?</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🔒</div>
              <h3 className="feature-title">No KYC Required</h3>
              <p className="feature-text">Exchange cryptocurrencies without any verification or personal data. Your privacy is our priority.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">⚡</div>
              <h3 className="feature-title">Best Rates</h3>
              <p className="feature-text">We aggregate rates from multiple partner exchanges to always provide you with the most competitive market rates.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🚀</div>
              <h3 className="feature-title">Instant Transactions</h3>
              <p className="feature-text">Fully automated system ensures your exchange is completed as quickly as the blockchain allows.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🌐</div>
              <h3 className="feature-title">1000+ Currencies</h3>
              <p className="feature-text">Support for Bitcoin, Ethereum, Monero, and thousands of other cryptocurrencies across all major networks.</p>
            </div>
          </div>
        </section>

        {/* Footer */}
        <div className="terminal-footer">
          <div className="terminal-footer-content">
            <div className="terminal-health-status">
              <div className="terminal-health-item">
                <span className={`terminal-health-indicator ${apiHealth}`}></span>
                <span className="terminal-health-label">API</span>
              </div>
              <div className="terminal-health-item">
                <span className={`terminal-health-indicator ${dbHealth}`}></span>
                <span className="terminal-health-label">DATABASE</span>
              </div>
            </div>
            <div className="terminal-links">
              <a href="https://changenow.io" target="_blank" rel="noopener noreferrer">POWERED BY CHANGENOW.IO</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
